import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Star, Settings as SettingsIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { profileCardService, ProfileCard } from '../lib/profileCardService';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

interface QuickFillButtonProps {
  onFill: (formattedText: string) => void;
  onReplace?: (card: ProfileCard) => void;
  disabled?: boolean;
  documentContent?: string;
}

const QuickFillButton: React.FC<QuickFillButtonProps> = ({ onFill, onReplace, disabled = false, documentContent = '' }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [cards, setCards] = useState<ProfileCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasPlaceholders, setHasPlaceholders] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const placeholders = profileCardService.detectPlaceholders(documentContent);
    setHasPlaceholders(placeholders.length > 0);
  }, [documentContent]);

  useEffect(() => {
    const loadCards = async () => {
      if (!isOpen) return;

      if (!user?.id) {
        console.warn('QuickFillButton: No user ID available', { user });
        return;
      }

      try {
        setLoading(true);
        console.log('QuickFillButton: Loading cards for user:', user.id);
        const { data, error } = await profileCardService.getAll(user.id);

        if (error) {
          console.error('QuickFillButton: Error loading cards:', error);
          throw error;
        }

        console.log('QuickFillButton: Loaded cards:', data);
        setCards(data || []);
      } catch (error) {
        console.error('QuickFillButton: Failed to load profile cards:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCards();
  }, [user?.id, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCardClick = (card: ProfileCard) => {
    if (hasPlaceholders && onReplace) {
      onReplace(card);
    } else {
      const formattedText = profileCardService.formatForDocument(card);
      onFill(formattedText);
    }
    setIsOpen(false);
  };

  const handleManageCards = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const defaultCard = cards.find(card => card.is_default);
  const sortedCards = defaultCard
    ? [defaultCard, ...cards.filter(card => !card.is_default)]
    : cards;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        variant="outline"
        className="w-full font-bold"
        disabled={disabled}
      >
        <UserPlus className="w-4 h-4 mr-2" />
        Quick Fill
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Select a Profile Card
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {hasPlaceholders
                ? 'Click to replace placeholders in your document'
                : 'Click to insert information at cursor position'}
            </p>
            {hasPlaceholders && (
              <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs text-blue-700 dark:text-blue-300">
                Placeholders detected - data will be auto-filled
              </div>
            )}
          </div>

          <div className="py-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              </div>
            ) : cards.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <UserPlus className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                  No profile cards yet
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Create cards in Settings to use Quick Fill
                </p>
                <button
                  onClick={handleManageCards}
                  className="text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                >
                  Go to Settings
                </button>
              </div>
            ) : (
              <>
                {sortedCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => handleCardClick(card)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 dark:text-gray-100 flex items-center">
                        {card.card_name}
                        {card.is_default && (
                          <Star className="w-3 h-3 ml-1 text-yellow-500 fill-yellow-500" />
                        )}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                      {card.profile_data.full_name && (
                        <div className="truncate">{card.profile_data.full_name}</div>
                      )}
                      {card.profile_data.email && (
                        <div className="truncate">{card.profile_data.email}</div>
                      )}
                      {card.profile_data.phone_number && (
                        <div className="truncate">{card.profile_data.phone_number}</div>
                      )}
                    </div>
                  </button>
                ))}

                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                  <button
                    onClick={handleManageCards}
                    className="flex items-center text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                  >
                    <SettingsIcon className="w-4 h-4 mr-1" />
                    Manage Cards
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickFillButton;
